import { logger } from '@automata/utils';
import { grey } from '@mui/material/colors';
import { FC } from 'react';
import {
  type ConnectionLineComponentProps,
  HandleElement,
  Position,
  getBezierPath,
} from 'reactflow';

import {
  DATA_HANDLE_IDENTIFIER,
  SOURCE_HANDLE_IDENTIFIER,
} from '../../constants';

const _isFromHandleSource = (fromHandle?: HandleElement) => {
  return fromHandle?.id?.includes(SOURCE_HANDLE_IDENTIFIER) ? true : false;
};

const _positions = (
  fromPosition: Position
): { sourcePosition: Position; targetPosition: Position } | undefined => {
  switch (fromPosition) {
    // creating a dependency between a labware and a task or a task and a task
    // is always a vertical connection from the bottom of one node to the top of the other.
    // if the fromPosition is either top or bottom, this is the kind of connection
    // we're making
    case Position.Top:
    case Position.Bottom:
      return { sourcePosition: Position.Bottom, targetPosition: Position.Top };
    // creating a time constraint is always from a handle on the right of a task node to
    // a handle on the right of another task node
    case Position.Right:
      return { sourcePosition: Position.Right, targetPosition: Position.Right };
    case Position.Left:
      logger.error(
        'Left position is not supported - have we added a new handle type?'
      );
  }
};

const dataConnectionOffset = 4;

export const WFBConnectionLine: FC<ConnectionLineComponentProps> = (props) => {
  const { fromX, fromY, toX, toY, fromHandle, fromPosition } = props;

  const isSourceDataNode = fromHandle
    ? fromHandle.id?.includes(DATA_HANDLE_IDENTIFIER)
    : false;

  const [edgePath] = getBezierPath({
    sourceX:
      (isSourceDataNode ? dataConnectionOffset : 0) +
      (_isFromHandleSource(fromHandle) ? fromX : toX),
    sourceY: _isFromHandleSource(fromHandle) ? fromY : toY,
    targetX:
      (isSourceDataNode ? dataConnectionOffset : 0) +
      (_isFromHandleSource(fromHandle) ? toX : fromX),
    targetY: _isFromHandleSource(fromHandle) ? toY : fromY,
    ..._positions(fromPosition),
  });

  return (
    <g stroke={grey[800]} strokeWidth={4}>
      <path fill="none" d={edgePath} />
      <circle
        cx={isSourceDataNode ? dataConnectionOffset + toX : toX}
        cy={isSourceDataNode ? toY + dataConnectionOffset : toY}
        fill="black"
        r={3}
      />
    </g>
  );
};
